'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { HAS_OPEN_ROLES } from '@/config/jobs';
import { atlasUseCases } from '@/config/products';

export default function Footer() {
  const [year, setYear] = useState<number | null>(null);

  useEffect(() => {
    setYear(new Date().getFullYear());
  }, []);

  return (
    <footer className="bg-zinc-950 border-t border-zinc-800 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:justify-between gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="flex flex-col gap-4 shrink-0">
            <div className="flex flex-col gap-4">
              <img
                src="/assets/logos/logo_wide_white.svg"
                alt="Elementix"
                width={156}
                height={24}
                className="h-6 w-auto self-start"
              />
              <p className="text-sm text-zinc-200">
                Borrower intelligence platform for lenders
              </p>
            </div>
            <div className="flex flex-col gap-2 text-sm text-zinc-200">
              <a href="mailto:contact@elementix.com" className="hover:text-white transition-colors">
                contact@elementix.com
              </a>
              <p>221 W 9th St #313<br />Wilmington, DE 19801</p>
            </div>
            <a
              href="https://trust.elementix.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Elementix SOC 2 Type 2 audit by Sensiba — visit our Trust Center"
              className="self-start rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
            >
              <img
                src="/assets/logos/sensiba-soc-2-type-2-white.png"
                alt="Sensiba SOC 2 Type 2"
                width={535}
                height={250}
                loading="lazy"
                className="h-auto w-40"
              />
            </a>
            <p className="text-xs text-zinc-400 mt-auto">
              Copyright © {year ?? ''} Elementix Inc
            </p>
          </div>

          {/* Spacer */}
          <div className="hidden md:block flex-1" />

          {/* Links Group - Right aligned */}
          <div className="grid grid-cols-3 gap-6 sm:gap-10 md:gap-16 shrink-0 md:ml-auto">
            {/* Product groups match the main navigation. */}
            <div className="flex flex-col gap-3 sm:gap-4">
              <h3 className="text-xs sm:text-sm font-medium text-white">Products</h3>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                <Link href="/" className="text-xs sm:text-sm font-medium text-zinc-100 hover:text-white">Atlas<span className="mt-1 block text-xs font-normal text-zinc-400">Open Data Portal</span></Link>
                <div className="flex flex-col gap-2.5 border-l border-zinc-800 pl-3">
                  {atlasUseCases.map(item => <Link key={item.href} href={item.href} className="text-xs text-zinc-200 hover:text-white">{item.label}</Link>)}
                </div>
                <Link href="/recon" className="mt-2 text-xs sm:text-sm font-medium text-zinc-100 hover:text-white">Recon<span className="mt-1 block text-xs font-normal text-zinc-400">Underwriting agent</span></Link>
              </div>
            </div>

            {/* Company Links */}
            <div className="flex flex-col gap-3 sm:gap-4">
              <h3 className="text-xs sm:text-sm font-medium text-white">Company</h3>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                <Link href="/technology" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">Technology</Link>
                <Link href="/faq" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  FAQ
                </Link>
                <Link href="/contact" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  Contact
                </Link>
                <Link href="/careers" className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  {HAS_OPEN_ROLES ? (
                    <>
                      <span aria-hidden="true" className="relative flex h-2 w-2 shrink-0">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping"></span>
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400"></span>
                      </span>
                      <span className="sr-only">We&apos;re hiring: </span>
                    </>
                  ) : null}
                  Careers
                </Link>
                <a href="https://www.linkedin.com/company/elementix-ai/" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  LinkedIn
                </a>
                <a href="/contact" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  Book a Demo
                </a>
              </div>
            </div>

            {/* Legal Links */}
            <div className="flex flex-col gap-3 sm:gap-4">
              <h3 className="text-xs sm:text-sm font-medium text-white">Legal</h3>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                <Link href="/privacy" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  Privacy Notice
                </Link>
                <Link href="/terms" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  Website Terms of Use
                </Link>
                <a href="https://trust.elementix.com" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-zinc-200 hover:text-white transition-colors">
                  Trust Center
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
